import React from 'react';
import PropTypes from 'prop-types';
import GeneralDialogLayout from '@Containers/Dialogs/GeneralDialogLayout/GeneralDialogLayout';
import externalUrl from '@Utils/ExternalUrl';
import isEmpty from 'lodash/isEmpty';
import { ESPACENET, USPTO } from '@Components/Results/constants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { getAnalyticsForFullTextModalCopy, getAnalyticsForFullTextModalLinks } from '@Components/Results/analytics/fullTextAnalytics';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';

const LINK_MAP = {
  [ESPACENET]: 'Espacenet',
  [USPTO]: 'US Patent',
};

const ARTICLE_MAP = {
  Auth: 'aulast',
  Journal: 'title',
  Volume: 'volume',
  'Publication Year': 'date',
  Issue: 'issue',
  PAGES: 'spage',
  CODEN: 'coden',
  ISSN: 'issn',
  DOI: 'doi',
};

const PATENT_MAP = {
  'Patent Number': 'pubno',
  'Patent kind code': 'kindcode',
  'Publication Date': 'pubdate',
};

function FullTextDialog({
  onClose,
  onCopy,
  isPatent,
  fields,
  links,
  citation,
  trackingAttributes,
  fullTextIndex,
  metadata,
  buttonElement,
}) {
  const mapConfig = isPatent ? PATENT_MAP : ARTICLE_MAP;
  const hasLinks = !isEmpty(links);
  const fieldsObject = Object.keys(mapConfig)?.map((param) => {
    const fieldName = mapConfig[param];
    return { label: `${param}`, value: fields[fieldName] };
  });
  const openLink = (optionLink) => {
    getAnalyticsForFullTextModalLinks({ optionLink, trackingAttributes, citation, links, fullTextIndex, metadata, buttonElement });
  };

  const handleCopy = () => {
    getAnalyticsForFullTextModalCopy();
    const stringToCopy = fieldsObject
      ?.map(
        (field) => `${field.label}:   ${field.value}
`
      )
      .join('');

    navigator.clipboard?.writeText(stringToCopy);
    onClose();
    onCopy();
  };

  const createFooter = () => {
    if (hasLinks) {
      return (
        <div className="els-btn-group-right full-text-dialog__footer">
          {Object.keys(links)?.map((item, index) => (
            <LinkElement
              data-e2e="full-text-link"
              key={LINK_MAP[item]}
              className="e2e-full-text"
              href={externalUrl(links[item]) || ''}
              tabIndex={index}
              isExternal
              onClick={() => openLink(LINK_MAP[item])}>
              <span key={item}>{LINK_MAP[item]}</span>
            </LinkElement>
          ))}
        </div>
      );
    }

    return (
      <div className="els-btn-group-right full-text-dialog__footer">
        <ButtonWithIcon
          data-e2e="full-text-dialog-copy"
          sprite="ChevronRight"
          type="link"
          linkIconSize="xs"
          iconRight
          className="e2e-full-text-dialog-copy"
          onClick={handleCopy}>
          Copy document details
        </ButtonWithIcon>
      </div>
    );
  };

  return (
    <GeneralDialogLayout size="sm" movable={false} className="full-text-dialog" footer={createFooter()} selfClose={onClose}>
      <div className="full-text-dialog__container">
        <div className="full-text-dialog__container_content">
          {fieldsObject.map((field) => (
            <div key={field.label} className="full-text-dialog__container_content_row">
              <div className="full-text-dialog__container_content_row_label">{field.label}:</div>
              <div className="full-text-dialog__container_content_row_value">{field.value}</div>
            </div>
          ))}
          {!hasLinks && (
            <div>
              <div> Link to the source of this document is not available.</div>
              <div className="full-text-dialog__container_content_info">
                {'Please request document via your Information Manager/\nlibrarian.'}
              </div>
            </div>
          )}
        </div>
      </div>
    </GeneralDialogLayout>
  );
}

FullTextDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  isPatent: PropTypes.bool,
  fields: PropTypes.object,
  links: PropTypes.object,
  citation: PropTypes.object,
  // for analytics
  trackingAttributes: PropTypes.object,
  fullTextIndex: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  metadata: MetadataPropTypesShape,
  buttonElement: PropTypes.instanceOf(Element),
};

export default FullTextDialog;
